// Subscription success - full white page, checkmark, email message

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { paymentService } from '../services/paymentService';
import { saveSubscription, trackOnboardingEvent } from '../services/supabaseService';
import { testCountTracker } from '../services/testCountTracker';
import { funnelEventTracker } from '../services/funnelEventTracker';
import { logger } from '../utils/logger';
import { THEORYCOACH_SUBSCRIPTION_SUCCESS_INSTALL } from '../constants/theorycoachEvents';
import { hasAuthenticatedSession } from '../utils/checkoutAuth';

const TEXTS = {
  en: {
    title: "You're all set!",
    message: "Redirecting you to your dashboard.",
    messageGuest: "Check your email to access your account.",
    messageAuthRequired: 'Sign in to attach this payment to your account.',
    cta: 'Go to dashboard',
    ctaGuest: 'Got it',
    ctaAuthRequired: 'Sign in to continue',
    redirecting: (n: number) => `Redirecting to dashboard in ${n}...`,
  },
  nl: {
    title: 'Alles is geregeld!',
    message: 'We sturen je door naar je dashboard.',
    messageGuest: 'Controleer je e-mail om je account te openen.',
    messageAuthRequired: 'Log in om deze betaling aan je account te koppelen.',
    cta: 'Naar dashboard',
    ctaGuest: 'Begrepen',
    ctaAuthRequired: 'Log in om verder te gaan',
    redirecting: (n: number) => `Redirect naar dashboard over ${n}...`,
  },
  ar: {
    title: 'كل شيء جاهز!',
    message: 'جاري توجيهك إلى لوحة التحكم.',
    messageGuest: 'تحقق من بريدك الإلكتروني للوصول إلى حسابك.',
    messageAuthRequired: 'سجّل الدخول لربط هذا الدفع بحسابك.',
    cta: 'انتقل إلى لوحة التحكم',
    ctaGuest: 'حسناً',
    ctaAuthRequired: 'سجّل الدخول للمتابعة',
    redirecting: (n: number) => `إعادة التوجيه إلى لوحة التحكم خلال ${n}...`,
  },
} as const;

type StoredSubscriptionPlan = 'weekly' | 'monthly' | 'lifetime';

const VALID_SUBSCRIPTION_TIERS = new Set<string>(['weekly', 'monthly', 'lifetime']);

function resolveStoredSubscriptionTier(
  planFromUrl: string | null,
  planFromVerify: string | undefined
): StoredSubscriptionPlan {
  const fromUrl = (planFromUrl || '').trim();
  if (fromUrl && VALID_SUBSCRIPTION_TIERS.has(fromUrl)) {
    return fromUrl as StoredSubscriptionPlan;
  }
  const fromVerify = (planFromVerify || '').trim();
  if (fromVerify && VALID_SUBSCRIPTION_TIERS.has(fromVerify)) {
    return fromVerify as StoredSubscriptionPlan;
  }
  return 'monthly';
}

export const SubscriptionSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { currentLanguage } = useLanguage();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'auth_required'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const installPromptEventSent = useRef(false);

  const lang = (currentLanguage === 'nl' ? 'nl' : currentLanguage === 'ar' ? 'ar' : 'en') as keyof typeof TEXTS;
  const t = TEXTS[lang];
  const isRtl = lang === 'ar';

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      setErrorMessage('No session ID. Please try again.');
      return;
    }

    const handleSuccess = async () => {
      try {
        const authed = await hasAuthenticatedSession();
        if (!authed) {
          setStatus('auth_required');
          setErrorMessage('Authentication required');
          return;
        }

        const result = await paymentService.handlePaymentSuccess(sessionId);
        if (!result.success) {
          setStatus('error');
          setErrorMessage(result.error || 'Payment verification failed.');
          return;
        }

        const resolvedPlan = resolveStoredSubscriptionTier(
          searchParams.get('plan'),
          result.planType
        );
        const months =
          result.months ??
          (resolvedPlan === 'lifetime' ? 999 : 1);
        const durableSubscriptionId = result.subscriptionId || `checkout_${sessionId}`;

        localStorage.setItem('subscriptionTier', resolvedPlan);
        localStorage.setItem('subscriptionId', durableSubscriptionId);
        localStorage.setItem('subscriptionStartDate', new Date().toISOString());
        localStorage.setItem('subscriptionMonths', String(months));

        let userId: string | null = null;
        const currentUserStr = localStorage.getItem('currentUser');
        if (currentUserStr) {
          try {
            const u = JSON.parse(currentUserStr);
            userId = u.id || null;
          } catch {
            // ignore
          }
        }
        if (userId) {
          await saveSubscription(userId, {
            stripeSubscriptionId: durableSubscriptionId,
            planType: resolvedPlan,
            currentPeriodEnd: undefined,
          });
          await trackOnboardingEvent(userId, 'subscription', { planType: resolvedPlan, months });
        }

        testCountTracker.markSubscriptionGatePassed();
        testCountTracker.invalidateCache();

        if (localStorage.getItem('aiExamSubscriptionGate') === 'true') {
          funnelEventTracker.subscriptionGatePassed?.();
          localStorage.removeItem('aiExamSubscriptionGate');
        }

        setIsLoggedIn(!!userId);
        setStatus('success');
      } catch (error) {
        logger.error('Subscription success error:', error);
        setStatus('error');
        setErrorMessage('An error occurred. Please contact support.');
      }
    };

    handleSuccess();
  }, [searchParams]);

  useEffect(() => {
    if (status !== 'success' || installPromptEventSent.current) return;
    installPromptEventSent.current = true;
    window.dispatchEvent(new CustomEvent(THEORYCOACH_SUBSCRIPTION_SUCCESS_INSTALL));
  }, [status]);

  // Auto redirect for logged-in users after 3 seconds
  useEffect(() => {
    if (status !== 'success' || !isLoggedIn) return;
    if (countdown <= 0) {
      navigate('/dashboard', { replace: true });
      return;
    }
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [status, isLoggedIn, countdown, navigate]);

  if (status === 'loading') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          padding: '2rem',
          fontFamily: 'Inter, sans-serif',
          background: '#ffffff',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid #e5e7eb',
            borderTopColor: '#1a3a5c',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <p style={{ marginTop: '1rem', color: '#6b7280' }}>Activating your subscription...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          padding: '2rem',
          fontFamily: 'Inter, sans-serif',
          textAlign: 'center',
          background: '#ffffff',
        }}
      >
        <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{errorMessage}</p>
        <div className="cta-bottom-wrap" style={{ width: '100%', maxWidth: 480, alignSelf: 'stretch' }}>
          <button
            type="button"
            className="cta-bottom-btn"
            onClick={() => navigate('/subscription')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#1a3a5c',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Back to Subscription
          </button>
        </div>
      </div>
    );
  }

  if (status === 'auth_required') {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          padding: '2rem',
          fontFamily: 'Inter, sans-serif',
          textAlign: 'center',
          background: '#ffffff',
        }}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <p style={{ color: '#374151', marginBottom: '1rem' }}>{t.messageAuthRequired}</p>
        <div className="cta-bottom-wrap" style={{ width: '100%', maxWidth: 480, alignSelf: 'stretch' }}>
          <button
            type="button"
            className="cta-bottom-btn"
            onClick={() => navigate('/signup', { state: { returnTo } })}
            style={{
              height: 52,
              background: '#0F2D5C',
              color: '#ffffff',
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t.ctaAuthRequired}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        padding: 24,
        boxSizing: 'border-box',
        fontFamily: 'Inter, sans-serif',
        textAlign: 'center',
        background: '#ffffff',
      }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div
        style={{
          width: 64,
          height: 64,
          background: '#16a34a',
          borderRadius: '50%',
          color: '#ffffff',
          fontSize: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
        }}
      >
        ✓
      </div>
      <h1
        style={{
          fontSize: 28,
          fontWeight: 800,
          color: '#1a1a1a',
          marginBottom: 8,
        }}
      >
        {t.title}
      </h1>
      <p
        style={{
          fontSize: 15,
          color: '#6b7280',
          textAlign: 'center',
          marginTop: isLoggedIn ? 0 : 16,
          marginBottom: isLoggedIn ? 8 : 32,
          lineHeight: 1.5,
        }}
      >
        {isLoggedIn ? t.message : t.messageGuest}
      </p>
      {isLoggedIn && countdown > 0 && (
        <p style={{ fontSize: 14, color: '#9ca3af', marginBottom: 24 }}>
          {t.redirecting(countdown)}
        </p>
      )}
      <div className="cta-bottom-wrap" style={{ width: '100%', maxWidth: 480, alignSelf: 'stretch' }}>
        <button
          type="button"
          className="cta-bottom-btn"
          onClick={() => navigate(isLoggedIn ? '/dashboard' : '/')}
          style={{
            height: 52,
            background: '#0F2D5C',
            color: '#ffffff',
            border: 'none',
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {isLoggedIn ? t.cta : t.ctaGuest}
        </button>
      </div>
    </div>
  );
};
